import { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight, Check, FileText } from 'lucide-react';
import { PRODUCTS } from '../store/productData';
import { useLanguage } from '../store/LanguageContext';
import { BRAND } from '../config/brand';
import { API_URL } from '../lib/api';

// V5.36 (feedback mede-eigenaar): aparte landingspagina per steen, zodat
// Google Shopping per product een eigen URL, prijs en attributen kan lezen.
// Elke pagina zet Product+Offer JSON-LD en een eigen title/description.
export default function ProductDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, language } = useLanguage();
  const product = PRODUCTS.find(p => p.slug === slug);
  // Live prijs uit de database (Filips locked_price) — de statische lijst
  // kan verouderd zijn en Google moet exact de echte prijs lezen.
  const [livePrice, setLivePrice] = useState<number | null>(null);
  useEffect(() => {
    if (!product) return;
    fetch(`${API_URL}/api/products`)
      .then(r => r.json())
      .then(j => {
        const hit = (j?.data ?? []).find((x: { slug?: string; locked_price?: number | null }) => x.slug === product.slug);
        if (hit?.locked_price != null) setLivePrice(hit.locked_price);
      })
      .catch(() => { /* statische prijs als vangnet */ });
  }, [product]);
  const price = livePrice ?? product?.pricePerM2 ?? 0;

  useEffect(() => {
    if (!product) return;
    document.title = `${product.name} — ${t('pdp.title.suffix')} | ${BRAND.name}`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', `${product.name}: ${t('pdp.metaDesc')} €${price}/m².`);

    // JSON-LD Product + Offer — de kern voor Shopping/rich results.
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.id = 'pdp-jsonld';
    ld.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      image: [`${window.location.origin}${product.images[0]}`],
      description: `${product.name} — ${t('pdp.metaDesc')}`,
      sku: product.serialNumber ?? product.slug,
      brand: { '@type': 'Brand', name: BRAND.name },
      offers: {
        '@type': 'Offer',
        url: window.location.href,
        priceCurrency: 'EUR',
        price,
        unitText: 'm²',
        availability: 'https://schema.org/InStock',
        seller: { '@type': 'Organization', name: BRAND.name },
      },
    });
    document.getElementById('pdp-jsonld')?.remove();
    document.head.appendChild(ld);
    return () => { document.getElementById('pdp-jsonld')?.remove(); };
  }, [product, t, language, price]);

  if (!product) return <Navigate to="/" replace />;

  const specs: Array<[string, string]> = [
    [t('pdp.spec.dimensions'), product.dimensions],
    [t('pdp.spec.color'), product.color],
    [t('pdp.spec.texture'), product.texture],
    [t('pdp.spec.category'), product.category],
    ...(product.serialNumber ? [[t('pdp.spec.sku'), product.serialNumber] as [string, string]] : []),
    ...(product.deliveryWeeks != null ? [[t('pdp.spec.delivery'), `±${product.deliveryWeeks} ${t('pdp.spec.weeks')}`] as [string, string]] : []),
  ];

  return (
    <div className="pt-16 md:pt-[72px] min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <nav className="text-xs text-[#8a7e74] mb-5">
          <Link to="/" className="hover:text-[#c47a3a]">{t('nav.home')}</Link>
          <span className="mx-1.5">/</span>
          <span className="text-[#1a1a1a] font-semibold">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 items-start">
          <div className="rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
            <img src={product.images[0]} alt={product.name} className="w-full aspect-[4/3] object-cover" />
          </div>

          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#1a1a1a]" style={{ fontFamily: 'Playfair Display' }}>
              {product.name}
            </h1>
            <p className="text-sm text-[#8a7e74] mt-1 capitalize">{product.color} · {product.category}</p>

            <div className="mt-4 flex items-baseline gap-2">
              <span className="text-3xl font-bold text-[#c47a3a]">€{price}</span>
              <span className="text-sm text-[#8a7e74]">/m² {t('pdp.exVat')}</span>
            </div>

            <div className="mt-6 border border-gray-100 rounded-xl overflow-hidden">
              {specs.map(([k, v], i) => (
                <div key={k} className={`flex justify-between gap-4 px-4 py-2.5 text-sm ${i % 2 === 0 ? 'bg-[#faf7f3]/60' : 'bg-white'}`}>
                  <span className="text-[#8a7e74]">{k}</span>
                  <span className="font-semibold text-[#1a1a1a] text-right capitalize">{v}</span>
                </div>
              ))}
            </div>

            <ul className="mt-5 space-y-1.5 text-sm text-[#4a4a4a]">
              {[t('pdp.usp1'), t('pdp.usp2'), t('pdp.usp3')].map(u => (
                <li key={u} className="flex items-start gap-2"><Check size={15} className="text-green-600 mt-0.5 flex-shrink-0" /> {u}</li>
              ))}
            </ul>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to={`/?product=${product.slug}#calculator`}
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#c47a3a] hover:bg-[#a8643a] text-white rounded-xl text-sm font-bold transition-colors"
              >
                {t('pdp.ctaCalc')} <ArrowRight size={15} />
              </Link>
              <Link
                to="/professioneel"
                className="inline-flex items-center gap-2 px-5 py-3 border border-gray-200 hover:border-[#c47a3a] text-[#4a4a4a] rounded-xl text-sm font-semibold transition-colors"
              >
                <FileText size={15} /> {t('pdp.ctaFiche')}
              </Link>
            </div>
          </div>
        </div>

        {/* Andere stenen — interne links helpen Google alle producten vinden */}
        <div className="mt-14">
          <h2 className="text-lg font-bold text-[#1a1a1a] mb-3">{t('pdp.more')}</h2>
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 gap-2">
            {PRODUCTS.filter(p => p.slug !== product.slug).slice(0, 16).map(p => (
              <Link key={p.id} to={`/steen/${p.slug}`} className="group rounded-lg overflow-hidden border border-gray-100 hover:border-[#c47a3a]/60 bg-white">
                <img src={p.images[0]} alt={p.name} className="w-full h-14 object-cover" loading="lazy" />
                <div className="p-1.5">
                  <p className="text-[10px] font-semibold truncate">{p.name}</p>
                  <p className="text-[10px] text-[#c47a3a] font-bold">€{p.pricePerM2}/m²</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
